import { S25Const } from "../s25-const";
import { jSith } from "../jquery-replacement";
import { Proto } from "../../pojo/Proto";
import { S25Bool } from "./s25.bool";
import { S25String } from "./s25.string";
import { isDefined } from "./index";
import EventLinkFormat = Proto.EventLinkFormat;

export namespace S25Url {
    /**
     * add query param, add url param, inject url parameter, inject property, inject url property or attribute
     * @return if param has a value, then queryString starting with &;
     * otherwise, returns the query you pass in
     */
    export function addQueryParam(param: string | undefined, searchQuery: string): string {
        if (!param) {
            return searchQuery;
        }

        const searchQueryParts: Array<string> = searchQuery.split("&").filter(S25Bool.toBool);
        const paramParts: Array<string> = param.split("&").filter(S25Bool.toBool);

        jSith.forEach(paramParts, function (_: any, part: string) {
            const paramParam: string = part.split("=")[0];
            const matches: Array<string> = searchQueryParts.filter(function (p: string) {
                return p.indexOf(paramParam + "=") > -1 || p === paramParam;
            });
            if (matches.length) {
                searchQueryParts.splice(searchQueryParts.indexOf(matches[0]), 1, part);
            } else {
                searchQueryParts.push(part);
            }
        });

        return "&" + searchQueryParts.join("&");
    }

    export function addParam(url: string, name: string, value?: string | number) {
        url += value ? (url.indexOf("?") > -1 ? "&" + name : "?" + name) + "=" + value : "";
        return url;
    }

    export function getMultiQuery(searchQuery: string | undefined, multiQueryStr: string): string {
        searchQuery = jSith.toStr(searchQuery);
        searchQuery = searchQuery.replace(/&events*_query_id=\d+/gi, "");
        searchQuery = searchQuery.replace(/&spaces*_query_id=\d+/gi, "");
        searchQuery = searchQuery.replace(/&resources*_query_id=\d+/gi, "");
        searchQuery = searchQuery.replace(/&organizations*_query_id=\d+/gi, "");
        searchQuery = searchQuery.replace(/&contacts*_query_id=\d+/gi, "");
        searchQuery = searchQuery.replace(/&query_id=\d+/gi, "");
        searchQuery += "&multi_query_id=" + multiQueryStr;
        return searchQuery;
    }

    export function getObjQueryId(itemTypeId: string) {
        let searchQuery;
        switch (itemTypeId) {
            case "1":
            case "event":
                searchQuery = "event_query_id=";
                break;
            case "2":
            case "organization":
                searchQuery = "organization_query_id=";
                break;
            case "4":
            case "location":
                searchQuery = "space_query_id=";
                break;
            case "6":
            case "resource":
                searchQuery = "resource_query_id=";
                break;
        }

        return searchQuery;
    }
    export function getWindowOrigin(): string {
        //mockable function call to get origin
        return window.location.origin;
    }

    export function getWindowPath(): string {
        // mockable function call to get pathname
        // remove embedded portion of the url if there is one, note that pathname does not end in a slash
        return window.location.pathname.replace(/\/embedded\/.*/, "");
    }

    export function getURL(postHostPath: string, postInstancePath: string): string {
        postHostPath = postHostPath || "/";
        postInstancePath = postInstancePath || "";
        let host: string = window.location.hostname;
        if (host === "localhost") {
            host = "25live.collegenet.com";
        }

        if (S25Const.isHostUriSeries25 && postHostPath && postHostPath !== "/") {
            postInstancePath = postHostPath;
            if (postInstancePath && postInstancePath.lastIndexOf("/") === postInstancePath.length - 1) {
                postInstancePath = postInstancePath.substr(0, postInstancePath.length - 1);
            }
            postHostPath = "/";
        }

        return window.location.protocol + "//" + host + postHostPath + S25Const.instanceId + postInstancePath;
    }

    export function isGauss(): boolean {
        return window.location.pathname.indexOf("25power-") > -1;
    }

    export function getEventDetailsUrl(eventId: string | number, app?: EventLinkFormat): string {
        //examples:
        //https://25live.collegenet.com/pro/wes#!/home/event/12441/details
        //https://25live.collegenet.com/wes/#details&obj_type=event&obj_id=24717
        //https://25live.collegenet.com/burnside/scheduling.html#/events/details/137977

        //note: we cannot use s25Const.baseUrl bc developer boxes are too different and also series25.com switches pro/instance to instance/pro
        //whereas origin + pathname is guaranteed to give us the proper base url from which we can tack on the routing hash in ALL environments
        let url: string = S25Url.getWindowOrigin() + S25Url.getWindowPath();
        app = app || "pro";
        if (app === "pro") {
            url += "#!/home/event/" + eventId + "/details";
        } else if (app === "25live") {
            url = url.replace("/pro/", "/"); //remove pro from url if present
            url = url.replace(/\/pro$/, ""); //remove pro from url if present
            url += "#details&obj_type=event&obj_id=" + eventId;
        } else if (app === "scheduling") {
            url = url.replace("/pro/", "/"); //remove pro from url if present
            url = url.replace(/\/pro$/, ""); //remove pro from url if present
            url = S25String.addSlash(url) + "scheduling.html";
            url += "#/events/details/" + eventId;
        }
        return url;
    }

    export function getOrganizationDetailsUrl(orgId: string | number): string {
        return S25Url.getWindowOrigin() + S25Url.getWindowPath() + "#!/home/organization/" + orgId + "/details";
    }

    export function toSeries25DataUrl(url: string) {
        if (S25Const.isHostUriSeries25 && url?.indexOf("25live.collegenet.com") > -1) {
            url = url.replace("https://25live.collegenet.com", getWindowOrigin);
            url = url.replace("http://25live.collegenet.com", getWindowOrigin);
            url = url.replace("/25live/data/", "/");
            url = url.replace("/run/", "/data/");
        }
        return url;
    }

    /**
     * Replace 25live.collegenet.com URLs with actual base URL for localhost/unival domains.
     * Also handles 25saas-[^\/]+ pattern preservation/removal.
     * @param url The URL to process
     * @returns The processed URL, or original URL if not on localhost/unival or doesn't contain 25live.collegenet.com
     */
    export function replace25LiveUrl(url: string): string {
        if (!url) return url;

        // Exit early if not on localhost or unival.com
        const isLocalHost = /localhost/.test(window.location.origin);
        const isUnival = /\.unival\.com/.test(window.location.origin);
        if (!isLocalHost && !isUnival) {
            return url;
        }

        // Exit early if URL doesn't contain 25live.collegenet.com
        if (!url.includes("25live.collegenet.com")) {
            return url;
        }

        // Extract the origin (protocol + hostname) - don't include pathname
        const hostname = window.location.hostname.replace(/\/25saas-[^\/]+/, "");
        const origin = window.location.protocol + "//" + hostname;

        // Extract 25saas pattern from current location's pathname if it exists
        const saasBranchMatch = window.location.pathname.match(/\/(25saas-[^\/]+)/);
        const saasBranch = saasBranchMatch ? "/" + saasBranchMatch[1] : "";

        // Replace the domain with origin and extract the path/query part
        const urlMatch = url.match(/https?:\/\/25live\.collegenet\.com(\/.*)/);
        if (!urlMatch) {
            return url;
        }

        let pathAndQuery = urlMatch[1];

        // Remove any 25saas pattern from the URL path itself (if it exists there)
        pathAndQuery = pathAndQuery.replace(/\/25saas-[^\/]+/, "");

        // If we have a saasBranch from current location, prepend it to the path
        if (saasBranch) {
            pathAndQuery = saasBranch + pathAndQuery;
        }

        return origin + pathAndQuery;
    }

    export function isValidUrl(urlString: string): boolean {
        try {
            new URL(urlString);
            return true;
        } catch {
            return false;
        }
    }

    export function eMailLink(address: string): string {
        return isDefined(address) ? `mailto:${address}` : "";
    }
}
